import {Injectable} from '@angular/core';
import {catchError, first, map, retry} from 'rxjs/operators';
import VideoClipModel from '../../../models/video-clip.model';
import {
    EmptyAction,
    ErrorVideoAction,
    SetInitialVideoValues,
    SetVideoStateTypeAction,
    SuccessGetVideoAction,
    UpdateForms,
    UpdateNavigateTimeAction,
    UpdateVideoTimeAction
} from '../stores/root-store/actions/video.action';
import {Observable, of, Subject, throwError} from 'rxjs';
import {
    selectCurrentFrameFromState,
    selectCurrentTimeFromState,
    selectVideoClipsFromState
} from '../stores/root-store/selectors/video.selector';
import {HttpService} from './http.service';
import {Store} from '@ngrx/store';
import VideoState from '../stores/root-store/video.state';
import {BrowserService} from './browser.service';
import {VideoStateEnum} from '../../../enums/video-state.enum';
import {FadeService} from './fade.service';
import {AnimationInitService} from './animation-init.service';
import {ErrorEnum} from '../../../enums/error.enum';
import {ActionService} from './action.service';
import {OverlayType} from '../../../enums/overlay-type.enum';
import {HttpErrorResponse} from '@angular/common/http';

@Injectable()
export class VideoService {
    public replayButtonPressed: Subject<boolean> = new Subject<boolean>();

    constructor(
        private httpService: HttpService,
        private browser: BrowserService,
        private fadeService: FadeService,
        private actionService: ActionService,
        private store: Store<{ video: VideoState }>,
        private animationInitService: AnimationInitService
    ) {
    }

    getVideo(id: string, params: Map<string, string>) {
        if (this.browser.isIE()) {
            return of(ErrorVideoAction({
                errorType: ErrorEnum.INCOMPATIBLE_BROWSER,
                error: {
                    message: 'Unsupported browser - IE'
                }
            }));
        }

        return this.httpService.getVideo(id, params).pipe(
            catchError((error: any) => {
                if((error as HttpErrorResponse).status === 502) {
                    this.store.dispatch(ErrorVideoAction({
                        errorType: ErrorEnum.HIGH_TRAFFIC_ERROR, error
                    }));
                    return throwError(() => new Error(error));
                }
            }),
            retry({
                count:2, delay: 5000
            }),
            map((data: any) => {
                let startState = VideoStateEnum.Buffering;

                if (this.browser.isAppleMobileDevice()) {
                    startState = VideoStateEnum.Pause;
                }

                for (const audioTrack of data.audioTracks) {
                    audioTrack.loaded = false;
                }

                return SuccessGetVideoAction({
                    payload: data, startState
                });
            }),
            catchError((error: any) => {
                let errorType = ErrorEnum.VIDEO_NOT_FOUND;

                if ((error as HttpErrorResponse).status > 500) {
                    errorType = ErrorEnum.TECHNICAL;
                }

                if (error.error && error.error.errorType === ErrorEnum.CREDITS_UNAVAILABLE) {
                    errorType = ErrorEnum.CREDITS_UNAVAILABLE;
                }

                if (error.error && error.error.errorType === ErrorEnum.VIDEO_UNAVAILABLE) {
                    errorType = ErrorEnum.VIDEO_UNAVAILABLE;
                }

                return of(ErrorVideoAction({
                    errorType, error
                }));
            })
        );
    }

    setFirstClipAction(payload) {
        payload.forms.forEach(form => {
            form.fields = this.animationInitService.createAnimationAndFadeSteps(this.increaseOverlayZIndex(form.fields));
        });

        return SetInitialVideoValues({
            currentScene: payload.videoClips[0],
            overlays: this.animationInitService.createAnimationAndFadeSteps(this.increaseOverlayZIndex(payload.overlays)),
            forms: payload.forms
        });
    }

    setVideoTimeUpdate(payload) {
        return this.store.select(selectVideoClipsFromState)
            .pipe(
                map(videoClips => {
                    const currentTime = this.calculatePreviousScenesTime(videoClips, payload.currentSceneId) + payload.currentTime;

                    return UpdateVideoTimeAction({
                        currentTime
                    });
                })
            );
    }

    setCurrentAndNextVideoScene(payload) {
        return this.store.select(selectVideoClipsFromState)
            .pipe(
                map((videoClips) => {
                    return this.setCurrentAndNextVideoSceneAction(payload, videoClips);
                })
            );
    }

    currentIndex(videoClips: VideoClipModel[], currentVideoId: number) {
        return videoClips.findIndex(video => video.id === currentVideoId);
    }

    calculatePreviousScenesTime(videoClips: VideoClipModel[], currentSceneId) {
        const currentIndex = this.currentIndex(videoClips, currentSceneId);
        return videoClips[currentIndex - 1] ? videoClips[currentIndex - 1].time.end : 0;
    }

    setCurrentAndNextVideoSceneAction(payload, videoClips: VideoClipModel[]) {
        const currentIndex = this.currentIndex(videoClips, payload.currentSceneId);
        const currentVideoClip = videoClips.length === currentIndex + 1 ? videoClips[0] : videoClips[currentIndex + 1];
        return SetInitialVideoValues({
            currentScene: currentVideoClip
        });
    }

    calculateTimeForward(): Observable<number> {
        return this.store.select(selectCurrentTimeFromState).pipe(
            first(),
            map(time => time += 10)
        );
    }

    calculateTimeBackward() {
        return this.store.select(selectCurrentTimeFromState).pipe(first(), map(time => {

            if (time - 10 > 0) {

                time -= 10;
                return UpdateNavigateTimeAction({
                    navigationTime: time
                });

            }

            // eslint-disable-next-line
      const random = Math.random() / 100;
            return UpdateNavigateTimeAction({
                navigationTime: random
            });
        }));
    }

    videoEndedActionHandler() {
        return this.store.select(selectCurrentFrameFromState).pipe(first(),
            map((frame) =>
                this.actionService.checkAndPerformAction(frame)
                    ? EmptyAction()
                    : SetVideoStateTypeAction({
                        videoState: VideoStateEnum.Ended
                    })
            ));
    }

    addSubmitToForms(payload) {
        payload.forms.forEach(form => {
            if (!form.fields || form.fields.length === 0) {
                return;
            }

            const formTimes = this.getMaxTimespan(form.fields);
            const submitOverlay = payload.overlays.find(overlay => overlay.type === 'submit' && (
                (formTimes.start >= overlay.time.start && formTimes.end <= overlay.time.end)
        || (overlay.time.start >= formTimes.start && overlay.time.end <= formTimes.end)));

            if (submitOverlay) {
                submitOverlay.time = this.getSubmitTimespan(form.fields);
                form.fields.push(submitOverlay);
            } else {
                form.fields.push({
                    uid: form.id + '_submit',
                    type: OverlayType.Submit,
                    time: this.getSubmitTimespan(form.fields),
                    style: {
                    }
                });
            }
        });

        return UpdateForms({
            forms: payload.forms
        });
    }

    private getMaxTimespan(fields) {
        return fields.reduce((value, field) => ({
            start: Math.min(field.time.start, value.start),
            end: Math.max(field.time.end, value.end)
        }), {
            start: Infinity, end: 0
        });
    }

    private getSubmitTimespan(fields): { start: number, end: number } {
        return fields.reduce((value, field) => ({
            start: Math.max(field.time.start, value.start),
            end: Math.max(field.time.end, value.end)
        }), {
            start: 0, end: 0
        });
    }

    private increaseOverlayZIndex(overlays) {
        return overlays.map((overlay) => {
            if (overlay && overlay.style && !overlay.uid.match(/fade/)) {
                overlay.style.zIndex = overlay.style.hasOwnProperty('zIndex') ? overlay.style.zIndex + 2 : 2;
            }

            return overlay;
        });
    }
}
